import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { CodeReferenceBlockHighlightRecord } from "models/view-models/code-reference-block-highlights/code-reference-block-highlight-record";
import { HighlightProperties } from "organisms/code-reference-block-editor/code-reference-preview";
import CodeReferenceHighlightViewModal from "organisms/highlight-modal/code-reference-highlight-view-modal";
import useActiveHighlightModal from "organisms/highlight-modal/useActiveHighlightModal";
import React, { RefObject, useState } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { HighlightColor } from "utilities/enumerations/highlight-color";

interface HighlightBadgeListProps {
    blockHighlights: CodeReferenceBlockHighlightRecord[];
    blockRefElement?: RefObject<HTMLDivElement>;
    onSave: (highlight: HighlightProperties) => Promise<void>;
    onColorChange: (newColor: HighlightColor) => void;
    onDelete: (highlightId: number | undefined) => Promise<void>;
}

const BASE_CLASS_NAME = "c-highlight-badge-list";

const HighlightBadgeList = ({
    blockHighlights,
    blockRefElement,
    onSave,
    onColorChange,
    onDelete,
}: HighlightBadgeListProps) => {
    const sortedHighlights = blockHighlights.sort((a, b) => {
        return a.sortIndex - b.sortIndex;
    });

    const [isViewModalVisible, setIsViewModalVisible] = useState(false);

    const {
        setActiveViewModal,
        closeActiveViewModal,
        activeViewHighlightModal,
    } = useActiveHighlightModal();

    const highlightCount = sortedHighlights.length ?? 0;

    const handleHighlightButtonClick = () => {
        setIsViewModalVisible(true);
        setActiveViewModal("viewList");
    };

    const handleCloseViewModal = () => {
        setIsViewModalVisible(false);
        closeActiveViewModal();
    };

    const handleDelete = async (highlightId: number | undefined) => {
        await onDelete(highlightId);
        handleCloseViewModal();
    };

    const highlightColors = Array.from(
        new Set(
            sortedHighlights.map((item) =>
                HighlightColor[item.highlightColor].toLowerCase()
            )
        )
    );

    const [firstColor, ...restOfColors] = highlightColors;
    return (
        <div data-no-highlight className={BASE_CLASS_NAME}>
            {CollectionUtils.hasValues(sortedHighlights) && (
                <Button
                    data-no-highlight
                    size={ButtonSizes.XSmall}
                    cssClassName={`${BASE_CLASS_NAME}__button`}
                    style={ButtonStyles.None}
                    onClick={handleHighlightButtonClick}>
                    <span
                        className={`${BASE_CLASS_NAME}__color -${firstColor}`}>
                        <Icon type={Icons.Highlighter} />
                        {highlightCount}
                    </span>
                    {restOfColors.map((color) => (
                        <span
                            key={color}
                            className={`${BASE_CLASS_NAME}__color -${color}`}
                        />
                    ))}
                </Button>
            )}
            {activeViewHighlightModal === "viewList" && isViewModalVisible && (
                <CodeReferenceHighlightViewModal
                    highlights={sortedHighlights}
                    blockRefElement={blockRefElement}
                    editType={"editFromViewList"}
                    onSave={onSave}
                    onColorChange={onColorChange}
                    onClose={handleCloseViewModal}
                    onDelete={handleDelete}
                />
            )}
        </div>
    );
};

export default HighlightBadgeList;
